import * as React from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";

export default function PortalSubscriptionsList({
  subscriptions,
  loading,
  error,
  /** Total matching rows from API (may exceed loaded `subscriptions` when capped by limit). */
  apiTotal,
  onRefresh,
  squarespaceCustomerId,
}) {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  React.useEffect(() => {
    setPage(0);
  }, [subscriptions]);

  const paginated = subscriptions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const loadedCount = subscriptions.length;

  const adminSubscriptionsHref = squarespaceCustomerId
    ? `/dashboard/subscriptions?squarespace_customer_id=${encodeURIComponent(squarespaceCustomerId)}`
    : "/dashboard/subscriptions";

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          Portal subscriptions
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            size="small"
            component={RouterLink}
            to={adminSubscriptionsHref}
            startIcon={<OpenInNewIcon />}
          >
            Open in Subscriptions
          </Button>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        BryteBridge subscriptions whose <code style={{ fontFamily: "monospace", fontSize: "0.85em" }}>meta_data.squarespace_customer_id</code> matches this Squarespace customer.
      </Typography>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : subscriptions.length === 0 ? (
        <Alert severity="info">
          No portal subscriptions linked to this customer ID. Add{" "}
          <code style={{ fontFamily: "monospace", fontSize: "0.85em" }}>squarespace_customer_id</code> to subscription meta_data to link them.
        </Alert>
      ) : (
        <>
        {typeof apiTotal === "number" && apiTotal > loadedCount ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Showing {loadedCount} of {apiTotal} matching subscriptions (max load per request is 100). Use{" "}
            <strong>Open in Subscriptions</strong> to page through all results.
          </Alert>
        ) : null}
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Source</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Organization</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>Renewal</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((subscription) => (
                <TableRow key={subscription.id} hover>
                  <TableCell>
                    {subscription.source != null && String(subscription.source).trim()
                      ? String(subscription.source)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={subscription.status || "Unknown"}
                      size="small"
                      color={
                        subscription.status === "active"
                          ? "success"
                          : subscription.status === "cancelled"
                            ? "error"
                            : subscription.status === "expired"
                              ? "warning"
                              : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>{subscription.dba_name || subscription.organization_name || "—"}</TableCell>
                  <TableCell>
                    {subscription.amount != null
                      ? `$${parseFloat(subscription.amount).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {subscription.start_date
                      ? new Date(subscription.start_date).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {subscription.renewal_date
                      ? new Date(subscription.renewal_date).toLocaleDateString()
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      component={RouterLink}
                      to={`/dashboard/subscriptions/${subscription.id}`}
                      size="small"
                      variant="outlined"
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={loadedCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Rows:"
          />
        </TableContainer>
        </>
      )}
    </>
  );
}
